import { getCache } from "@vercel/functions";

const CACHE_NAMESPACE = "print";

// A hung cache call must never cost more than the upstream call it replaces.
const CACHE_TIMEOUT_MS = 1000;

// Platform cap is 2 MB per item; stay well under it so a borderline payload is
// dropped by us with a log line instead of being rejected by the platform.
const MAX_CACHED_VALUE_BYTES = 1024 * 1024;

export const PRESET_TTL_SECONDS = 6 * 60 * 60;
export const PRODUCT_DATA_TTL_SECONDS = 6 * 60 * 60;
export const RAW_PRICE_TTL_SECONDS = 60 * 60;

// Bump when the cached payload shape changes: entries live for hours, so a bad
// shape would otherwise outlive the deploy that fixed it.
const KEY_VERSION = "v1";

const loggedReasons = new Set();
const inflight = new Map();

export function skuTag(sku) {
  return `sku:${sku}`;
}

function logOnce(reason, error) {
  if (loggedReasons.has(reason)) return;
  loggedReasons.add(reason);
  console.warn(`[print-runtime-cache] ${reason}:`, error?.message || error);
}

// Off Vercel there is no platform bridge and getCache() silently degrades to a
// per-process Map, which would make local dev and jest behave unlike production.
function isRuntimeCacheAvailable() {
  return Boolean(process.env.VERCEL);
}

function openCache() {
  if (!isRuntimeCacheAvailable()) return null;
  try {
    return getCache({ namespace: CACHE_NAMESPACE });
  } catch (error) {
    logOnce("cache unavailable", error);
    return null;
  }
}

function withTimeout(operation, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${CACHE_TIMEOUT_MS}ms`)),
      CACHE_TIMEOUT_MS
    );
  });
  return Promise.race([operation, timeout]).finally(() => clearTimeout(timer));
}

async function readCache(cache, key) {
  try {
    const value = await withTimeout(
      Promise.resolve(cache.get(key)),
      "cache.get"
    );
    return value === null ? undefined : value;
  } catch (error) {
    logOnce("cache read failed", error);
    return undefined;
  }
}

// Empty containers are the failure sentinels of the print helpers (`{}` from
// fetchProductDataFromPrintApi, `null` from fetchMainPrintProductPreset), so
// caching them would pin a product as unpriceable for the whole TTL.
function isCacheWorthy(value) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function measureBytes(value) {
  const serialized = JSON.stringify(value);
  if (typeof serialized !== "string") return 0;
  return typeof Buffer === "undefined"
    ? serialized.length
    : Buffer.byteLength(serialized, "utf8");
}

async function writeCache(cache, key, value, { ttl, tags, name }) {
  let valueBytes;
  try {
    valueBytes = measureBytes(value);
  } catch (error) {
    logOnce("cache value not serializable", error);
    return;
  }

  if (valueBytes > MAX_CACHED_VALUE_BYTES) {
    logOnce("cache value too large", `${valueBytes} bytes for ${key}`);
    return;
  }

  try {
    await withTimeout(
      Promise.resolve(cache.set(key, value, { ttl, tags, name })),
      "cache.set"
    );
  } catch (error) {
    logOnce("cache write failed", error);
  }
}

async function resolveThroughCache(cache, key, options, producer) {
  const cached = await readCache(cache, key);
  if (cached !== undefined) return cached;

  const value = await producer();
  if (isCacheWorthy(value)) await writeCache(cache, key, value, options);
  return value;
}

export async function cachedJson(key, options = {}, producer) {
  const cache = openCache();
  if (!cache) return producer();

  const running = inflight.get(key);
  if (running) return running;

  const run = resolveThroughCache(cache, key, options, producer);
  inflight.set(key, run);
  try {
    return await run;
  } finally {
    inflight.delete(key);
  }
}

// Exported so a batch caller and the read-through helper below provably build
// the same physical key; two buckets for one value would split invalidation
// is about.
export function presetCacheKey(sku) {
  return `preset:${KEY_VERSION}:${sku}`;
}

export function productDataCacheKey({ sku, view, locale }) {
  return `product-data:${KEY_VERSION}:${sku}:${view ?? ""}:${locale ?? ""}`;
}

// priceKey carries its own version, backend, user, config, currency and region.
export function rawPriceCacheKey(priceKey) {
  return `raw-price:${priceKey}`;
}

// Reads N keys in one pass. Misses are absent from the Map rather than present
// as undefined, so the caller can tell "cached nothing" from "not cached".
export async function getMany(keys) {
  const hits = new Map();
  const cache = openCache();
  if (!cache) return hits;

  await Promise.all(
    keys.map(async (key) => {
      const value = await readCache(cache, key);
      if (value !== undefined) hits.set(key, value);
    })
  );
  return hits;
}

// Best-effort and independent per entry: one unwritable row must not drop its
// siblings, and nothing here ever throws.
export async function setMany(entries, { ttl, tags, name } = {}) {
  const cache = openCache();
  if (!cache) return;

  await Promise.all(
    entries.map(async (entry) => {
      if (!entry?.key || !isCacheWorthy(entry.value)) return;
      await writeCache(cache, entry.key, entry.value, {
        ttl,
        tags: entry.tags ?? tags,
        name: entry.name ?? name,
      });
    })
  );
}

export async function expireSku(sku) {
  if (!sku) return false;

  const cache = openCache();
  if (!cache) return false;

  try {
    await withTimeout(
      Promise.resolve(cache.expireTag(skuTag(sku))),
      "cache.expireTag"
    );
    return true;
  } catch (error) {
    logOnce("cache expiration failed", error);
    return false;
  }
}

export async function cachedPreset(sku, producer) {
  return cachedJson(
    presetCacheKey(sku),
    {
      ttl: PRESET_TTL_SECONDS,
      tags: [skuTag(sku)],
      name: "print-preset",
    },
    producer
  );
}

export async function cachedProductData({ sku, view, locale }, producer) {
  return cachedJson(
    productDataCacheKey({ sku, view, locale }),
    {
      ttl: PRODUCT_DATA_TTL_SECONDS,
      tags: [skuTag(sku)],
      name: "print-product-data",
    },
    producer
  );
}

export async function cachedRawPrice(priceKey, { sku }, producer) {
  return cachedJson(
    rawPriceCacheKey(priceKey),
    {
      ttl: RAW_PRICE_TTL_SECONDS,
      tags: sku ? [skuTag(sku)] : [],
      name: "print-raw-price",
    },
    producer
  );
}
